<template>
  <div class="bg-gray-50">
    <!-- Botão Flutuante do Chat + Balão de Conversa -->
    <div class="fixed bottom-6 right-6 flex items-center space-x-2" style="z-index: 9999;">
      
      <!-- Balão de Conversa (Aparece antes do usuário abrir o chat) -->
      <transition name="fade">
        <div v-if="!isOpen && showMessageBubble" class="bg-white shadow-lg p-3 rounded-lg text-gray-800 text-sm font-semibold animate-bounce">
          👋 Oi! Precisa de ajuda?
        </div>
      </transition>

      <!-- Botão do Chat -->
      <button @click="toggleChat"
        class="bg-blue-500 text-white p-4 rounded-full shadow-lg hover:bg-blue-600 transition flex items-center">
        
        <!-- Ícone de Chat -->
        <svg v-if="!isOpen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" clip-rule="evenodd" 
            d="M12 2C6.48 2 2 6.48 2 12c0 2.34.8 4.5 2.15 6.24L4 22l3.9-.9A9.98 9.98 0 0012 22c5.52 0 10-4.48 10-10S17.52 2 12 2zM7 9h10v2H7V9zm0 4h6v2H7v-2z"/>
        </svg>

        <!-- Ícone de "X" ao abrir -->
        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
          <path stroke="white" stroke-width="2" d="M6 6L18 18M6 18L18 6"/>
        </svg>

      </button>
    </div>

    <!-- Chat Flutuante -->
    <transition name="fade">
      <div v-if="isOpen"
        class="fixed bottom-20 right-6 bg-white w-80 rounded-xl shadow-2xl border border-gray-300 transition-all ease-in-out mb-5">
        
        <!-- Cabeçalho do Chat -->
        <div class="flex justify-between items-center border-b pb-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-t-lg">
          <div class="flex items-center space-x-2">
            <img src="/img/logo-branco.png" class="w-10" alt="Bot Avatar">
            <h3 class="text-lg font-semibold font-uppercase">Entre em contato</h3>
          </div>
        </div>

        <!-- Área do Chat -->
        <div ref="chatContainer" class="pt-4 space-y-3 text-sm flex flex-col overflow-y-auto h-72 p-3 bg-gray-50 rounded-lg">
          <div v-for="(msg, index) in chatMessages" :key="index" class="flex items-end"
            :class="msg.sender === 'bot' ? 'justify-start' : 'justify-end'">
            
            <!-- Avatar do Robô -->
            <img v-if="msg.sender === 'bot'" src="/img/robo.jpg" class="w-9 h-9 rounded-full border mr-2 shadow-md">

            <!-- Mensagem do Chat -->
            <div class="p-3 rounded-lg max-w-[75%] shadow-md text-sm transition-opacity duration-300 ease-in-out"
              :class="msg.sender === 'bot' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'">
              {{ msg.text }}
            </div>

            <!-- Avatar do Usuário -->
            <div v-if="msg.sender === 'user'" 
              class="w-9 h-9 rounded-full bg-gray-500 text-white flex items-center justify-center font-bold ml-2 shadow-md font-uppercase">
              {{ userInitials }}
            </div>
          </div>

          <!-- Efeito "Digitando..." -->
          <div v-if="isTyping" class="flex items-center space-x-2 text-gray-500 text-xs mt-2">
            <img src="/img/robo.jpg" class="w-6 h-6 rounded-full">
            <span>Digitando...</span>
          </div>
        </div>

        <!-- Entrada de Respostas do Usuário com Botão de Enviar -->
        <div v-if="currentStep < 2" class="pt-4 p-4 bg-gray-50 rounded-lg flex items-center space-x-2">
          <input v-model="userResponses[currentStep]" :placeholder="placeholders[currentStep]"
            class="w-full p-2 border rounded-lg text-sm focus:outline-none focus:ring focus:border-blue-300"
            @keypress.enter="nextStep" />
          
          <!-- Botão de Enviar -->
          <button @click="nextStep"
            class="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 21l21-9-21-9v7l15 2-15 2v7z"/>
            </svg>
          </button>
        </div>

        <!-- Botão Final de WhatsApp -->
        <div v-if="currentStep === 2" class="pt-4 flex flex-col space-y-2 p-4 bg-gray-50 rounded-lg">
          <p class="text-sm text-gray-700 text-center">Agora clique no botão abaixo para falar com um especialista. 🚀</p>
          <a :href="whatsappLink" target="_blank"
            class="block bg-green-500 text-white px-4 py-2 rounded-lg text-center font-semibold hover:bg-green-600 transition">
            📲 Chamar no WhatsApp
          </a>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';

const isOpen = ref(false);
const currentStep = ref(0);
const userResponses = ref(["", ""]);
const chatMessages = ref([
  { text: "👋 Olá! Eu sou seu assistente virtual. Como posso te chamar?", sender: "bot" }
]);

const placeholders = ["Digite seu nome...", "Digite seu WhatsApp (DDD) 99999-9999"];
const whatsappNumber = "5519999041297";
const chatContainer = ref(null);
const isTyping = ref(false);
const showMessageBubble = ref(true);

// Abre/fecha o chat e esconde o balão de mensagem
const toggleChat = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    showMessageBubble.value = false;
  }
};

// Calcula as iniciais do usuário
const userInitials = computed(() => {
  if (!userResponses.value[0]) return "U";
  const names = userResponses.value[0].split(" ");
  return names.length > 1 ? names[0][0] + names[1][0] : names[0][0];
});

// Avança para a próxima etapa e mantém todas as mensagens
const nextStep = () => {
  if (userResponses.value[currentStep.value].trim() === "") return;

  chatMessages.value.push({ text: userResponses.value[currentStep.value], sender: "user" });
  isTyping.value = true;
  
  setTimeout(() => {
    isTyping.value = false;
    if (currentStep.value === 0) {
      chatMessages.value.push({ text: `Ótimo, ${userResponses.value[0]}! Agora me informe seu WhatsApp:`, sender: "bot" });
      currentStep.value++;
    } else if (currentStep.value === 1) {
      chatMessages.value.push({ text: "Obrigado! Agora clique no botão abaixo para falar com um especialista. 🚀", sender: "bot" });
      currentStep.value++;
    }
    scrollToBottom();
  }, 1500);
};

// Faz o chat rolar automaticamente para a última mensagem
const scrollToBottom = () => {
  nextTick(() => {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  });
};
</script>
